'use client';

import React, { useEffect, useRef, useId } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { cn } from '../lib/utils';

export interface AlertDialogProps {
  open: boolean;
  onClose: () => void;
  /** Title — appears as dialog heading. */
  title: string;
  /** Description — optional body text above actions. */
  description?: React.ReactNode;
  /** Confirm button label. Default "Confirm". */
  confirmLabel?: string;
  /** Cancel button label. Default "Cancel". */
  cancelLabel?: string;
  /** Called when user clicks the confirm button. */
  onConfirm: () => void | Promise<void>;
  /** Destructive styling (red confirm button). Use for delete/close/revoke. */
  destructive?: boolean;
  /** Show loading spinner on confirm button. */
  loading?: boolean;
  /**
   * Disable the confirm button without the loading spinner (e.g. an
   * attestation checkbox that has not been ticked yet). Cancel stays enabled.
   */
  confirmDisabled?: boolean;
  /** Optional extra content rendered between description and actions. */
  children?: React.ReactNode;
}

/**
 * Styled alternative to `window.confirm()`. Use for destructive or
 * consequential actions (delete document, close incident, revoke access).
 *
 * Standard voice:
 *   title: verb + object (e.g., "Delete document", "Close incident")
 *   description: what happens + recovery path ("This will remove the file.
 *                 You can restore it within 30 days from Audit Log.")
 *   confirmLabel: the verb ("Delete", "Close incident")
 *
 * Built on the same pattern as Dialog — portal, focus trap, escape-to-close.
 */
export function AlertDialog({
  open,
  onClose,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  onConfirm,
  destructive = false,
  loading = false,
  confirmDisabled = false,
  children,
}: AlertDialogProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const confirmButtonRef = useRef<HTMLButtonElement>(null);
  const cancelButtonRef = useRef<HTMLButtonElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const onCloseRef = useRef(onClose);
  const loadingRef = useRef(loading);
  const dialogId = useId();
  const titleId = `${dialogId}-title`;
  const descId = `${dialogId}-description`;

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    loadingRef.current = loading;
  }, [loading]);

  useEffect(() => {
    if (!open) return;

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape' && !loadingRef.current) {
        onCloseRef.current();
        return;
      }
      if (e.key === 'Tab') {
        const focusable = dialogRef.current?.querySelectorAll(
          'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        );
        if (!focusable?.length) return;
        const first = focusable[0] as HTMLElement;
        const last = focusable[focusable.length - 1] as HTMLElement;
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;

    previousFocusRef.current = document.activeElement as HTMLElement;
    document.body.style.overflow = 'hidden';
    // Focus the cancel button for destructive dialogs (WAI-ARIA Authoring
    // Practices §3.2 — least destructive action receives initial focus to
    // prevent accidental Enter-key confirmation of a destructive action).
    // Non-destructive dialogs still focus cancel — it's the safe default.
    const focusFrame = requestAnimationFrame(() => {
      cancelButtonRef.current?.focus();
    });

    return () => {
      cancelAnimationFrame(focusFrame);
      document.body.style.overflow = '';
      previousFocusRef.current?.focus();
    };
  }, [open]);

  if (!open) return null;

  const handleConfirm = async () => {
    await onConfirm();
  };

  const content = (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={description ? descId : undefined}
    >
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm"
        onClick={loading ? undefined : onClose}
        aria-hidden="true"
      />
      <div
        ref={dialogRef}
        tabIndex={-1}
        className={cn(
          'relative z-10 flex max-h-[calc(100vh-2rem)] w-full max-w-md flex-col overflow-hidden rounded-lg bg-white shadow-xl',
          'animate-in fade-in-0 zoom-in-95 duration-150',
        )}
      >
        <div className="flex min-h-0 flex-1 items-start gap-4 overflow-y-auto overscroll-y-contain px-6 py-6">
          {destructive && (
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-100">
              <AlertTriangle className="h-5 w-5 text-red-600" aria-hidden="true" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h2 id={titleId} className="font-heading text-lg font-semibold text-statura-ink">
              {title}
            </h2>
            {description && (
              <div id={descId} className="mt-2 text-sm text-slate-600">
                {description}
              </div>
            )}
            {children && <div className="mt-4">{children}</div>}
          </div>
        </div>
        <div className="flex shrink-0 flex-col items-stretch gap-2 border-t border-slate-200 bg-slate-50 px-6 py-4 sm:flex-row sm:items-center sm:justify-end sm:gap-3">
          <button
            ref={cancelButtonRef}
            type="button"
            onClick={onClose}
            disabled={loading}
            className={cn(
              'inline-flex h-10 items-center justify-center rounded-lg border border-slate-300 bg-white px-4 text-sm font-medium text-statura-ink',
              'transition-colors hover:bg-slate-50',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-statura-green/40',
              'disabled:cursor-not-allowed disabled:opacity-50',
            )}
          >
            {cancelLabel}
          </button>
          <button
            ref={confirmButtonRef}
            type="button"
            onClick={handleConfirm}
            disabled={loading || confirmDisabled}
            className={cn(
              'inline-flex h-10 items-center justify-center gap-2 rounded-lg px-4 text-sm font-semibold text-white',
              'transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
              destructive
                ? 'bg-red-600 hover:bg-red-700 focus-visible:ring-red-500'
                : 'bg-statura-green hover:bg-statura-green-accessible focus-visible:ring-statura-green',
              'disabled:cursor-not-allowed disabled:opacity-60',
            )}
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );

  if (typeof window === 'undefined') return null;
  return createPortal(content, document.body);
}
